import { useState } from 'react';
import { Button, Card } from '@/components/ui';
import { useT } from '@/i18n';

export interface PlanPromptPanelProps {
  childName: string;
  busy?: boolean;
  /** Receives the focus, or undefined when the parent skips. */
  onSubmit: (prompt?: string) => void;
}

const MAX = 400;

/**
 * Step one of asking for the plan: what the parent wants it to focus on.
 *
 * It comes before payment deliberately. The plan is the thing being bought, so
 * the parent should shape it before paying for it, not discover afterwards that
 * they were never asked. Skipping is fine and produces the plan as before.
 */
export const PlanPromptPanel = ({ childName, busy, onSubmit }: PlanPromptPanelProps) => {
  const t = useT();
  const [prompt, setPrompt] = useState('');

  return (
    <Card className="space-y-4">
      <div>
        <h1 className="text-h2 text-ink">{t('plan.prompt.title', { child: childName })}</h1>
        <p className="mt-2 text-body text-ink-muted">{t('plan.prompt.body')}</p>
      </div>

      <label className="block">
        <span className="sr-only">{t('plan.prompt.label')}</span>
        <textarea
          className="min-h-[140px] w-full rounded-lg border-2 border-line bg-white p-3 text-body text-ink focus:border-sage-300"
          maxLength={MAX}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value.slice(0, MAX))}
          placeholder={t('plan.prompt.placeholder')}
          disabled={busy}
        />
      </label>
      <p className="text-right text-caption text-ink-muted">{t('q.note.counter', { n: prompt.length })}</p>

      <div className="flex flex-wrap gap-3">
        <Button loading={busy} onClick={() => onSubmit(prompt.trim() || undefined)}>
          {t('plan.prompt.continue')}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => onSubmit(undefined)}>
          {t('plan.prompt.skip')}
        </Button>
      </div>
    </Card>
  );
};
